import { useState } from "react";
import { useData } from "../../context/DataContext";
import { Card, Btn, NativeSelect } from "../kit";
import { fmtINR, computePL, periodRange, plWhatsApp, BUCKET_LABEL } from "../../lib/finance";
import { toast } from "sonner";
import { Copy } from "lucide-react";

export default function ProfitLoss() {
  const { entries } = useData();
  const [period, setPeriod] = useState("fy");
  const { label } = periodRange(period);
  const { b, grossProfit, totalOpex, netProfitBeforeTax, netProfitAfterTax } = computePL(entries, period);

  const copy = () => {
    navigator.clipboard.writeText(plWhatsApp(entries, period))
      .then(() => toast.success("Copied — paste straight into WhatsApp"))
      .catch(() => toast.error("Could not copy"));
  };

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <NativeSelect value={period} onChange={(e) => setPeriod(e.target.value)} className="!w-auto" data-testid="pnl-period-selector">
          <option value="month">This month</option>
          <option value="quarter">Last 3 months</option>
          <option value="fy">FY 2026-27 (Apr'26–Mar'27)</option>
          <option value="all">All time</option>
        </NativeSelect>
        <Btn variant="outline" onClick={copy} data-testid="pnl-whatsapp-copy-button"><Copy className="h-4 w-4" /> WhatsApp summary</Btn>
      </div>

      <h2 className="text-xl font-bold text-slate-900 font-head mt-5">Profit &amp; Loss — {label}</h2>

      <div className="mt-4 space-y-1 text-sm" data-testid="pnl-statement">
        <Section>Revenue</Section>
        <Line l={BUCKET_LABEL.sales} v={fmtINR(b.sales)} />
        <Line l={BUCKET_LABEL.cogs} v={`(${fmtINR(b.cogs)})`} />
        <Sub l="Gross Profit" v={fmtINR(grossProfit)} />

        <Section>Operating Expenses</Section>
        <Line l={BUCKET_LABEL.snm} v={`(${fmtINR(b.snm)})`} />
        <Line l={BUCKET_LABEL.rnd} v={`(${fmtINR(b.rnd)})`} />
        <Line l={BUCKET_LABEL.gna} v={`(${fmtINR(b.gna)})`} />
        <Line l={BUCKET_LABEL.other_opex} v={`(${fmtINR(b.other_opex)})`} />
        <Sub l="Total Operating Expenses" v={`(${fmtINR(totalOpex)})`} />

        <Section>Other</Section>
        <Line l={`${BUCKET_LABEL.other_income} (incl. Brand Collabs)`} v={fmtINR(b.other_income)} />

        <div className="flex justify-between items-center rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 mt-3 font-bold text-slate-900">
          <span>Net Profit (before tax expenses)</span><span className="tnum">{fmtINR(netProfitBeforeTax)}</span>
        </div>

        <Section>Tax-purpose Expenses</Section>
        <Line l={`${BUCKET_LABEL.taxes} (e.g. petrol, kept for filing)`} v={`(${fmtINR(b.taxes)})`} />

        <div className="flex justify-between items-center rounded-xl bg-primary text-primary-foreground px-4 py-3.5 mt-3 font-bold text-base">
          <span>Net Profit (after tax expenses)</span><span className="tnum">{fmtINR(netProfitAfterTax)}</span>
        </div>

        <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
          Figures are cash-basis — client balances owed are not counted as revenue until received. "Tax-purpose expenses" (e.g. petrol) are shown separately from core operations.
        </p>
      </div>
    </Card>
  );
}

function Section({ children }) { return <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 pt-4 pb-1">{children}</p>; }
function Line({ l, v }) { return <div className="flex justify-between py-1 text-slate-600"><span>{l}</span><span className="tnum">{v}</span></div>; }
function Sub({ l, v }) { return <div className="flex justify-between py-1.5 border-t border-slate-100 font-semibold text-slate-900"><span>{l}</span><span className="tnum">{v}</span></div>; }
